/**
 * extend.gr — theme JavaScript.
 *
 * No jQuery, no framework. Everything here is progressive enhancement: the site
 * is fully readable and navigable with this file blocked. Motion respects
 * prefers-reduced-motion, which the design tokens already zero out.
 */
import '../css/main.css';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * NavBar — transparent until 24px of scroll, then glass + hairline border.
 */
function initHeader() {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  const THRESHOLD = 24;
  let ticking = false;

  const apply = () => {
    header.dataset.scrolled = String(window.scrollY > THRESHOLD);
    ticking = false;
  };

  apply();
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    },
    { passive: true }
  );
}

/**
 * Mobile menu. The panel is a plain element toggled by aria-expanded; focus is
 * trapped only while it is open, and Escape closes it.
 */
function initMobileMenu() {
  const toggle = document.querySelector('[data-menu-toggle]');
  const panel = document.querySelector('[data-menu-panel]');
  if (!toggle || !panel) return;

  const setOpen = (open) => {
    toggle.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    document.documentElement.classList.toggle('overflow-hidden', open);
    if (open) {
      const first = panel.querySelector('a, button');
      if (first) first.focus();
    }
  };

  toggle.addEventListener('click', () => {
    setOpen(toggle.getAttribute('aria-expanded') !== 'true');
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
      setOpen(false);
      toggle.focus();
    }
  });

  // Keep state honest when the viewport grows past the mobile breakpoint.
  window.matchMedia('(min-width: 1024px)').addEventListener('change', (e) => {
    if (e.matches) setOpen(false);
  });
}

/**
 * ServiceTabs — repurposed for /erp/integrations and /erp/industries.
 * Implements the APG tabs pattern with arrow-key roving focus.
 */
function initTabs() {
  document.querySelectorAll('[data-tabs]').forEach((root) => {
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    if (!tabs.length) return;

    const select = (index, focus = true) => {
      tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (panels[i]) panels[i].hidden = !active;
      });
      if (focus) tabs[index].focus();
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i, false));
      tab.addEventListener('keydown', (e) => {
        const map = {
          ArrowRight: (i + 1) % tabs.length,
          ArrowLeft: (i - 1 + tabs.length) % tabs.length,
          Home: 0,
          End: tabs.length - 1,
        };
        if (!(e.key in map)) return;
        e.preventDefault();
        select(map[e.key]);
      });
    });

    select(Math.max(0, tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')), false);
  });
}

/**
 * Scroll reveal — the only entrance motion on the site. Anything not revealed
 * within one frame stays visible, so content never depends on the observer.
 */
function initReveal() {
  const targets = document.querySelectorAll('.reveal');
  if (!targets.length) return;

  if (reduceMotion.matches || !('IntersectionObserver' in window)) {
    targets.forEach((el) => (el.dataset.revealed = 'true'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.dataset.revealed = 'true';
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
  );

  targets.forEach((el) => io.observe(el));
}

/**
 * Gravity Forms: move focus to the error summary on failed submit so the
 * failure is announced rather than silently rendered above the fold.
 */
function initFormErrors() {
  const focusSummary = () => {
    const summary = document.querySelector(
      '.gform_validation_errors, .gform_submission_error'
    );
    if (!summary) return;
    summary.setAttribute('tabindex', '-1');
    summary.focus();
  };

  focusSummary();
  document.addEventListener('gform_post_render', focusSummary);
}

const boot = () => {
  initHeader();
  initMobileMenu();
  initTabs();
  initReveal();
  initFormErrors();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

/**
 * Pause illustrative video for anyone who asked for reduced motion.
 *
 * CSS can stop an animation but cannot stop a video, and `autoplay` is an
 * attribute rather than a style — so without this, the one group of people who
 * explicitly asked for no motion get the largest moving thing on the page.
 *
 * The attribute is removed as well as the video paused: a browser that
 * re-evaluates autoplay on becoming visible would otherwise start it again.
 */
(() => {
  const quiet = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  if (!quiet?.matches) return;

  document.querySelectorAll('video[data-motion]').forEach((video) => {
    video.removeAttribute('autoplay');
    video.removeAttribute('loop');
    video.pause();
    // Show the first frame rather than an empty box.
    video.setAttribute('preload', 'metadata');
    video.controls = true;
  });
})();
